import { useState } from "react";
import { t } from "../i18n";

export default function EmailLoginModal({ lang, onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1 = email, 2 = code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSendCode(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/send-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || t(lang, "sendCodeFailed") || "Failed to send code");
      } else {
        setStep(2);
      }
    } catch {
      setError("Network error, please try again");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    if (!code.trim() || code.length !== 6) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || t(lang, "invalidCode") || "Invalid code");
      } else {
        localStorage.setItem("fm_token", data.token);
        onSuccess(data);
        onClose();
      }
    } catch {
      setError("Network error, please try again");
    } finally {
      setLoading(false);
    }
  }

  const title = step === 1 ? t(lang, "emailLoginTitle") : t(lang, "verifyCodeTitle");
  const desc = step === 1
    ? t(lang, "emailLoginDesc")
    : t(lang, "verifyCodeDesc").replace("{email}", email);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-10 pt-10 pb-6">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <p className="mt-2 text-sm text-gray-500 leading-relaxed">{desc}</p>
        </div>

        {/* Form */}
        <div className="px-10 pb-10">
          {step === 1 ? (
            <form onSubmit={handleSendCode} className="space-y-5">
              <div>
                <input
                  type="email"
                  placeholder={t(lang, "emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 transition text-gray-900 placeholder-gray-400 text-base"
                  autoFocus
                />
              </div>
              {error && (
                <p className="text-sm text-red-500">{error}</p>
              )}
              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full py-3.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-300 text-white font-semibold transition text-base"
              >
                {loading ? t(lang, "sending") : t(lang, "sendCode")}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full py-2.5 text-sm text-gray-500 hover:text-gray-700 transition"
              >
                {t(lang, "cancel")}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-5">
              <div>
                <input
                  type="text"
                  placeholder={t(lang, "codePlaceholder")}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  maxLength={6}
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 transition text-center text-2xl tracking-widest font-mono text-gray-900 placeholder-gray-300"
                  autoFocus
                />
              </div>
              {error && (
                <p className="text-sm text-red-500 text-center">{error}</p>
              )}
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full py-3.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-300 text-white font-semibold transition text-base"
              >
                {loading ? t(lang, "verifying") : t(lang, "verify")}
              </button>
              <button
                type="button"
                onClick={() => { setStep(1); setCode(""); setError(""); }}
                className="w-full py-2.5 text-sm text-gray-500 hover:text-gray-700 transition"
              >
                {t(lang, "useDifferentEmail")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
